import { ExclamationTriangleIcon, PhotoIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAtom } from "jotai";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    ALLOWED_ERROR_REPORT_MIMES,
    ERROR_REPORT_SEVERITIES,
    ErrorReportSeverity,
    errorReportSlideoverAtom,
    errorReportFileToBase64,
    MAX_ERROR_REPORT_IMAGES,
    ERROR_REPORT_SEVERITY_LABELS,
    useErrorReports,
    validateImageFile,
} from "../../services";
import { Button } from "../UI/button/Button.component";
import { Slideover } from "../UI/slideover/Slideover.component";
import { Textarea } from "../UI/textarea/Textarea.component";
import { captureContext } from "./captureContext";
import { MyErrorReports } from "./MyErrorReports.component";

interface PendingImage {
    file: File;
    previewUrl: string;
}

const MAX_DESCRIPTION_LENGTH = 5000;

// #region ReportErrorSlideover
// Global "Report an Error" form. Lives in the app shell so any authenticated
// user can reach it from any page; the current page URL and browser context are
// captured automatically at submit time.
export const ReportErrorSlideover: React.FunctionComponent = (): JSX.Element => {

    const [open, setOpen] = useAtom(errorReportSlideoverAtom);
    const [, , errorReportFunctions] = useErrorReports();

    const [tab, setTab] = useState<'report' | 'mine'>('report');
    const [description, setDescription] = useState<string>('');
    const [severity, setSeverity] = useState<ErrorReportSeverity>('annoying');
    const [images, setImages] = useState<PendingImage[]>([]);
    const [dragging, setDragging] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState<boolean>(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Object URLs must be revoked or the browser leaks the blob for the session.
    useEffect(() => {
        return () => { images.forEach((image) => URL.revokeObjectURL(image.previewUrl)); };
        // eslint-disable-next-line
    }, []);

    // #region resetForm
    const resetForm = useCallback(() => {
        images.forEach((image) => URL.revokeObjectURL(image.previewUrl));
        setDescription('');
        setSeverity('annoying');
        setImages([]);
        setFormError(null);
        setSubmitted(false);
        setTab('report');
    }, [images]);

    // #region closeSlideover
    const closeSlideover = (value: boolean) => {
        setOpen(value);
        if (!value) resetForm();
    };

    // #region addFiles
    // Validates type/size/count client-side so the user is never left waiting on
    // an upload that the server will reject anyway.
    const addFiles = (incoming: FileList | File[]) => {
        const files = Array.from(incoming);
        if (files.length === 0) return;

        const room = MAX_ERROR_REPORT_IMAGES - images.length;
        if (room <= 0) {
            setFormError(`You can attach at most ${MAX_ERROR_REPORT_IMAGES} screenshots.`);
            return;
        }

        const accepted: PendingImage[] = [];
        for (const file of files.slice(0, room)) {
            const problem = validateImageFile(file);
            if (problem) {
                setFormError(problem);
                continue;
            }
            accepted.push({ file, previewUrl: URL.createObjectURL(file) });
        }

        if (files.length > room) setFormError(`Only the first ${room} screenshot${room === 1 ? '' : 's'} were added — the limit is ${MAX_ERROR_REPORT_IMAGES}.`);
        if (accepted.length > 0) setImages((previous) => [...previous, ...accepted]);
    };

    // #region removeImage
    const removeImage = (index: number) => {
        setImages((previous) => {
            const target = previous[index];
            if (target) URL.revokeObjectURL(target.previewUrl);
            return previous.filter((_, i) => i !== index);
        });
        setFormError(null);
    };

    // #region handleDrop
    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragging(false);
        if (event.dataTransfer?.files) addFiles(event.dataTransfer.files);
    };

    // #region handleSubmit
    const handleSubmit = async () => {
        setFormError(null);
        if (description.trim().length === 0) {
            setFormError('Please tell us what went wrong.');
            return;
        }

        setSubmitting(true);
        try {
            const encoded = await Promise.all(
                images.map(async (image) => ({ data: await errorReportFileToBase64(image.file), name: image.file.name }))
            );

            const result = await errorReportFunctions.submitErrorReport({
                description: description.trim(),
                severity,
                images: encoded,
                context: captureContext(),
            });

            if (result.ok) {
                images.forEach((image) => URL.revokeObjectURL(image.previewUrl));
                setImages([]);
                setDescription('');
                setSubmitted(true);
            } else {
                setFormError(result.message);
            }
        } catch {
            setFormError('We could not read one of your screenshots. Try removing it and sending again.');
        } finally {
            setSubmitting(false);
        }
    };

    // #region renderThankYou
    if (submitted) {
        return (
            <Slideover open={open} setOpen={closeSlideover} slideoverTitle="Report an Error">
                <div className="flex flex-col items-center text-center gap-4 py-10">
                    <ExclamationTriangleIcon className="w-12 h-12 text-emerald-500" />
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">Thanks — we got it.</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 max-w-sm">
                        The team has been emailed and will take a look. You can track the status of your reports below.
                    </p>
                    <div className="flex gap-2">
                        <Button color="dark/zinc" onClick={() => { setSubmitted(false); setTab('mine'); }}>View my reports</Button>
                        <Button outline onClick={() => setSubmitted(false)}>Report another</Button>
                    </div>
                </div>
            </Slideover>
        );
    }

    return (
        <Slideover open={open} setOpen={closeSlideover} slideoverTitle="Report an Error">
            <div className="flex gap-2 border-b border-stone-200 dark:border-stone-700 mb-4">
                {([['report', 'New Report'], ['mine', 'My Reports']] as const).map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setTab(key)}
                        className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === key
                            ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {tab === 'mine' ? <MyErrorReports /> : (
                <div className="flex flex-col gap-5 pb-10">
                    <div>
                        <label htmlFor="error-report-description" className="block text-sm font-medium text-gray-900 dark:text-gray-200 mb-1">
                            What went wrong? <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            Tell us what you were doing and what you expected to happen. The page you&apos;re on and your browser details are attached automatically.
                        </p>
                        <Textarea
                            id="error-report-description"
                            rows={6}
                            maxLength={MAX_DESCRIPTION_LENGTH}
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            placeholder="e.g. Clicking Save on the design details page does nothing and the spinner never stops."
                        />
                        <p className="text-xs text-gray-400 mt-1 text-right">{description.length}/{MAX_DESCRIPTION_LENGTH}</p>
                    </div>

                    <fieldset>
                        <legend className="block text-sm font-medium text-gray-900 dark:text-gray-200 mb-2">
                            How badly is this affecting you? <span className="text-red-500">*</span>
                        </legend>
                        <div className="flex flex-col gap-2">
                            {ERROR_REPORT_SEVERITIES.map((option) => (
                                <label
                                    key={option}
                                    className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer text-sm ${severity === option
                                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                                        : 'border-stone-200 dark:border-stone-700 hover:border-stone-300 dark:hover:border-stone-600'}`}
                                >
                                    <input
                                        type="radio"
                                        name="error-report-severity"
                                        value={option}
                                        checked={severity === option}
                                        onChange={() => setSeverity(option)}
                                        className="text-emerald-600 focus:ring-emerald-500"
                                    />
                                    <span className="text-gray-800 dark:text-gray-200">{ERROR_REPORT_SEVERITY_LABELS[option]}</span>
                                </label>
                            ))}
                        </div>
                    </fieldset>

                    <div>
                        <span className="block text-sm font-medium text-gray-900 dark:text-gray-200 mb-1">
                            Screenshots <span className="font-normal text-gray-500 dark:text-gray-400">(optional, up to {MAX_ERROR_REPORT_IMAGES})</span>
                        </span>
                        <div
                            onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 cursor-pointer transition-colors ${dragging
                                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                                : 'border-stone-300 dark:border-stone-600 hover:border-stone-400 dark:hover:border-stone-500'}`}
                        >
                            <PhotoIcon className="w-8 h-8 text-gray-400" />
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Drag images here, or <span className="text-emerald-600 dark:text-emerald-400 font-medium">browse</span>
                            </p>
                            <p className="text-xs text-gray-400">PNG, JPEG, WebP or GIF · max 5MB each</p>
                        </div>
                        {/* A plain file input (not a drag-only zone) so macOS/Safari can always pick files. */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            multiple
                            accept={ALLOWED_ERROR_REPORT_MIMES.join(',')}
                            onChange={(event) => {
                                if (event.target.files) addFiles(event.target.files);
                                event.target.value = '';
                            }}
                        />

                        {images.length > 0 ? (
                            <div className="grid grid-cols-3 gap-3 mt-3">
                                {images.map((image, index) => (
                                    <div key={image.previewUrl} className="relative group">
                                        <img src={image.previewUrl} alt={image.file.name} className="h-24 w-full object-cover rounded-lg border border-stone-200 dark:border-stone-700" />
                                        <button
                                            type="button"
                                            onClick={() => removeImage(index)}
                                            aria-label={`Remove ${image.file.name}`}
                                            className="absolute -top-2 -right-2 rounded-full bg-stone-800 text-white p-1 shadow hover:bg-red-600"
                                        >
                                            <XMarkIcon className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>

                    {formError ? (
                        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                            {formError}
                        </div>
                    ) : null}

                    <div className="flex justify-end gap-2">
                        <Button outline onClick={() => closeSlideover(false)}>Cancel</Button>
                        <Button color="dark/zinc" onClick={handleSubmit} disabled={submitting}>
                            {submitting ? 'Sending…' : 'Send Report'}
                        </Button>
                    </div>
                </div>
            )}
        </Slideover>
    );
};
